import { DateTime } from "luxon";
import { toast } from "react-toastify";
import { useCopyToClipboard } from "usehooks-ts";
import { ClipboardIcon, EnvelopeIcon } from "@heroicons/react/24/outline";

import { Modal } from "../../../../../common/Atoms/Modal";
import { Paragraph } from "../../../../../common/Atoms/Typography/Paragraph";
import { Notification } from "../../../../../common/Atoms/Notification";
import { Spinner } from "../../../Common/Spinner";
import {
  useFetchInboundAttachmentAddressQuery,
  useFetchInboundAttachmentIssuesQuery,
  useRetryInboundAttachmentMutation,
} from "../../../../services/api/settingsApi/settings";

/** Why a received PDF could not be attached, in the customer's words */
const holdReasonLabels: Record<string, string> = {
  prefix_mismatch: `File name did not contain your prefix`,
  no_invoice_number: `No invoice number in the file name`,
  no_match: `No invoice matched that number`,
  multiple_matches: `More than one invoice matched`,
  recipient_mismatch: `Not sent to the contact's email address`,
  not_a_pdf: `File was not a PDF`,
};

interface InboundInvoiceEmailModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  organisationId: string;
}

export function InboundInvoiceEmailModal({ open, setOpen, organisationId }: InboundInvoiceEmailModalProps) {
  const [, copy] = useCopyToClipboard();

  const { data, isFetching } = useFetchInboundAttachmentAddressQuery(organisationId, {
    skip: !open || !organisationId,
  });

  const { data: issuesData, refetch: refetchIssues } = useFetchInboundAttachmentIssuesQuery(organisationId, {
    skip: !open || !organisationId,
    refetchOnMountOrArgChange: true,
  });

  const issues = issuesData?.issues || [];
  const [retryAttachment, { isLoading: isRetrying }] = useRetryInboundAttachmentMutation();

  async function onRetry(id: string) {
    try {
      const { matched } = await retryAttachment({ organisationId, id }).unwrap();

      if (matched) {
        toast.success(`Attached to the matching invoice`);
        refetchIssues();
      }
      else {
        toast.info(`Still no matching invoice for that file`);
      }
    }
    catch {
      toast.error(`Could not retry that attachment`);
    }
  }

  async function handleCopy() {
    if (!data?.address) {
      return;
    }

    try {
      await copy(data.address);
      toast.success(`Copied to clipboard`, { autoClose: 200 });
    }
    catch {
      toast.error(`Failed to copy to clipboard`);
    }
  }

  return (
    <Modal
      open={ open }
      setOpen={ setOpen }
      title={ `Email your invoices in` }
      icon={ <EnvelopeIcon className={ `text-paidniceGreen-500 h-5 w-5 mr-2` } /> }
    >
      <div className={ `space-y-4` }>
        <Paragraph>
          { `If your invoices are generated by another system, BCC the address below when that system emails the invoice to your customer. Paidnice will attach the PDF to the matching invoice and send that instead of the accounting system's PDF.` }
        </Paragraph>

        { isFetching || !data ? (
          <div className={ `flex justify-center py-2` }>
            <Spinner />
          </div>
        ) : (
          <div className={ `flex items-center justify-between bg-gray-100 rounded-md p-3 break-all` }>
            <code className={ `text-xs text-gray-800 mr-2` }>
              { data.address }
            </code>
            <ClipboardIcon
              className={ `h-5 w-5 shrink-0 text-paidniceGreen-500 cursor-pointer` }
              onClick={ handleCopy }
            />
          </div>
        ) }

        <Paragraph
          variant={ `secondary` }
        >
          { `The invoice number must appear in the PDF file name (e.g. Invoice_INV0017.pdf), and the email must also go to the customer's email address on the invoice.` }
        </Paragraph>

        <Notification
          type={ `warning` }
          title={ `Keep this address private` }
        >
          { `Anyone with this address can attach PDFs to your invoices, so don't share it publicly.` }
        </Notification>

        { issues.length > 0 && (
          <div>
            <Paragraph variant={ `secondary` }>
              { `Recently received and not attached. These are retried automatically for two days in case the invoice had not synced yet.` }
            </Paragraph>
            <div className={ `mt-2 overflow-hidden rounded-md border border-gray-200` }>
              <table className={ `min-w-full divide-y divide-gray-200 text-left text-xs` }>
                <thead className={ `bg-gray-50 text-gray-500` }>
                  <tr>
                    <th className={ `px-3 py-2 font-medium` }>{ `Received` }</th>
                    <th className={ `px-3 py-2 font-medium` }>{ `File` }</th>
                    <th className={ `px-3 py-2 font-medium` }>{ `Why` }</th>
                    <th className={ `px-3 py-2` }><span className={ `sr-only` }>{ `Retry` }</span></th>
                  </tr>
                </thead>
                <tbody className={ `divide-y divide-gray-100 bg-white` }>
                  { issues.map(issue => (
                    <tr key={ issue.id }>
                      <td className={ `whitespace-nowrap px-3 py-2 text-gray-500` }>
                        { DateTime.fromISO(issue.receivedAt).toFormat(`d LLL, HH:mm`) }
                      </td>
                      <td className={ `px-3 py-2 break-all text-gray-800` }>
                        { issue.fileName }
                        { issue.extractedInvoiceNumber ? (
                          <span className={ `block text-gray-400` }>
                            { `Read as ${issue.extractedInvoiceNumber}` }
                          </span>
                        ) : null }
                      </td>
                      <td className={ `px-3 py-2 text-gray-600` }>
                        { issue.holdReason ? holdReasonLabels[issue.holdReason] || issue.holdReason : `Unknown` }
                      </td>
                      <td className={ `px-3 py-2 text-right` }>
                        { issue.canRetry ? (
                          <button
                            type={ `button` }
                            className={ `font-medium text-paidniceGreen-600 hover:text-paidniceGreen-700 hover:underline cursor-pointer disabled:cursor-not-allowed disabled:text-gray-400` }
                            onClick={ () => onRetry(issue.id) }
                            disabled={ isRetrying }
                          >
                            { `Try again` }
                          </button>
                        ) : null }
                      </td>
                    </tr>
                  )) }
                </tbody>
              </table>
            </div>
          </div>
        ) }
      </div>
    </Modal>
  );
}
